/**
 * Image
 *
 * This class contains helper methods to load an image from a URL
 * and check whether it meets given dimension constraints
 *
 * @author rajeev@yext.com
 */
goog.module('yext.image');
goog.module.declareLegacyNamespace();

const yext = goog.require('yext');

const MIN_DIMENSIONS_ERROR = 'MIN_DIMENSIONS_ERROR';
const MAX_DIMENSIONS_ERROR = 'MAX_DIMENSIONS_ERROR';
const MAX_PIXELS_ERROR = 'MAX_PIXELS_ERROR';

/**
 * Returns true is given dimensions are valid
 *
 * @param {!Object} constraints
 * @param {!Object} imageDimensions
 * @return {boolean} true is image has valid dimensions based on given constraints
 */
function areValidDimensions(constraints, imageDimensions) {
  return checkMinDimensions(constraints, imageDimensions)
         && checkMaxDimensions(constraints, imageDimensions)
         && checkMaxPixels(constraints, imageDimensions);
}

/**
 * Returns an error if the given dimensions are not valid
 *
 * @param {!Object} constraints
 * @param {!Object} imageDimensions
 * @return {string} relevant error identifier, or empty if there is no error
 */
function getDimensionsError(constraints, imageDimensions) {
  if (constraints.aspectRatio == 1) {
    if (!checkSquareMinDimensions(constraints, imageDimensions)) {
      // We need both dimensions to be too small for invalid square image
      return MIN_DIMENSIONS_ERROR;
    }
  } else if (!checkMinDimensions(constraints, imageDimensions)) {
    return MIN_DIMENSIONS_ERROR;
  } else if (!checkMaxDimensions(constraints, imageDimensions)) {
    return MAX_DIMENSIONS_ERROR;
  } else if (!checkMaxPixels(constraints, imageDimensions)) {
    return MAX_PIXELS_ERROR;
  }

  return '';
}

/**
 * Returns true if given dimensions are >= the min dimensions
 *
 * @param {!Object} constraints
 * @param {!Object} imageDimensions
 * @return {boolean} true if image has valid dimensions based on min dimension constraints
 */
var checkMinDimensions = function(constraints, imageDimensions) {
  var minWidth = constraints.width ? constraints.width : 0;
  var minHeight = constraints.height ? constraints.height : 0;

  return imageDimensions.width >= minWidth && imageDimensions.height >= minHeight;
};

/**
 * Returns true if given dimensions are <= the max dimesions
 *
 * @param {!Object} constraints
 * @param {!Object} imageDimensions
 * @return {boolean} true if image has valid dimensions based on max dimension constraints
 */
var checkMaxDimensions = function(constraints, imageDimensions) {
  return (!constraints.maxWidth || constraints.maxWidth >= imageDimensions.width)
         && (!constraints.maxHeight || constraints.maxHeight >= imageDimensions.height);
};

/**
 * returns true if given any of the dimensions are <= min Dimensions
 *
 * @param {!Object} constraints
 * @param {!Object} imageDimensions
 * @return {boolean} true if image has ANY valid dimensions based on max dimension constraints
 */
var checkSquareMinDimensions = function(constraints, imageDimensions) {
  var minWidth = constraints.width ? constraints.width : 0;
  var minHeight = constraints.height ? constraints.height : 0;

  return imageDimensions.width >= minWidth || imageDimensions.height >= minHeight;
};

/**
 * Returns true if given dimensions are <= the max total pixels
 *
 * @param {!Object} constraints
 * @param {!Object} imageDimensions
 * @return {boolean} true if image has valid dimensions based on max pixel constraints
 */
var checkMaxPixels = function(constraints, imageDimensions) {
  return !constraints.maxPixels
         || (imageDimensions.width * imageDimensions.height) <= constraints.maxPixels;
};

/**
 * Get image dimensions
 *
 * @param {string} url
 * @param {function()} cb Callback called with the image dimensions
 */
function getImageDimensions(url, cb) {
  if (!yext.isFunction(cb)) {
    throw new TypeError('image: Unable to get image dimensions');
  }
  var $hiddenImage = $('<img>', {
    'src': url,
    'style': 'visibility: hidden',
  });
  $hiddenImage.appendTo('body');
  $hiddenImage.on('load', function() {
    var imageDimensions = {
      width: parseInt($hiddenImage.width()),
      height: parseInt($hiddenImage.height()),
    };
    $hiddenImage.remove();
    cb(imageDimensions);
  });
}

exports = {
  MIN_DIMENSIONS_ERROR,
  MAX_DIMENSIONS_ERROR,
  MAX_PIXELS_ERROR,
  areValidDimensions,
  getDimensionsError,
  getImageDimensions,
};
